/*
 * Updated BookingsList component
 * - Table width set to 80vw and centered in the container
 * - Removed booking code column from the table
 * - Added reschedule booking functionality
 * - Added Telegram notification when admin cancels a booking
 * - Restricted delete functionality to superadmins only
 * - Added commission column showing per-booking platform commission based on tour settings
 */

import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Calendar, Users, Mail, Phone, DollarSign, Clock, MapPin, Trash2, Eye, Search, Filter, Plus, CalendarClock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import LoadingSpinner from '../LoadingSpinner';
import { formatDate } from '../../utils/dateUtils';
import CreateBookingModal from './CreateBookingModal';

interface Booking {
  id: string;
  tour_id: string;
  booking_date: string;
  start_time: string;
  end_time: string | null;
  group_type: 'group' | 'reduced' | 'private';
  persons: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  pickup_address: string;
  total_price: number;
  payment_method: string;
  booking_code: string;
  status: 'not_confirmed' | 'confirmed' | 'cancelled';
  boot_sizes: string[];
  notes: string | null;
  created_at: string;
  tours: {
    id: string;
    name: string;
    commission_type: 'percentage' | 'per_person' | null;
    commission_value: number | null;
  };
}

const COMMISSION_RATE = 0.085;

const calculateCommission = (booking: Booking): number => {
  const tour = booking.tours;
  if (!tour || !tour.commission_type || tour.commission_value == null) return 0;
  if (tour.commission_type === 'percentage') {
    return (tour.commission_value || 0) * (booking.total_price || 0);
  }
  return COMMISSION_RATE * (tour.commission_value * (booking.persons || 0));
};

const getCommissionBreakdown = (booking: Booking): string => {
  const tour = booking.tours;
  if (!tour || !tour.commission_type || tour.commission_value == null) return 'No commission set';
  if (tour.commission_type === 'percentage') {
    const pct = (tour.commission_value * 100).toFixed(1);
    return `${pct}% of ${booking.total_price.toFixed(2)}`;
  }
  const perPerson = tour.commission_value.toFixed(2);
  return `8.5% of ${perPerson} x ${booking.persons} ppl`;
};

interface Tour {
  id: string;
  name: string;
  departure: string;
  group_tour_price: number;
  reduced_group_price: number;
  private_price: number;
}

const BookingsList: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tours, setTours] = useState<Tour[]>([]);
  const [rescheduleBooking, setRescheduleBooking] = useState<Booking | null>(null);
  const [newBookingDate, setNewBookingDate] = useState('');
  const [newStartTime, setNewStartTime] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    fetchBookings();
    fetchTours();
    checkAdminRole();
  }, []);

  const fetchBookings = async () => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          tours (
            id,
            name,
            commission_type,
            commission_value
          )
        `)
        .order('booking_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBookings(data || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTours = async () => {
    try {
      const { data, error } = await supabase
        .from('tours')
        .select('id, name, departure, group_tour_price, reduced_group_price, private_price')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setTours(data || []);
    } catch (error) {
      console.error('Error fetching tours:', error);
    }
  };

  const checkAdminRole = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data, error } = await supabase
        .from('admin_users')
        .select('role')
        .eq('id', session.user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching admin role:', error);
        return;
      }

      setIsSuperAdmin(data?.role === 'superadmin');
    } catch (error) {
      console.error('Error checking admin role:', error);
    }
  };

  const updateBookingStatus = async (bookingId: string, newStatus: 'confirmed' | 'cancelled') => {
    try {
      const booking = bookings.find(b => b.id === bookingId);

      const { error } = await supabase
        .from('bookings')
        .update({ status: newStatus })
        .eq('id', bookingId);

      if (error) throw error;

      setBookings(bookings.map(b =>
        b.id === bookingId ? { ...b, status: newStatus } : b
      ));

      if (selectedBooking?.id === bookingId) {
        setSelectedBooking({ ...selectedBooking, status: newStatus });
      }

      if (newStatus === 'cancelled' && booking) {
        await sendCancellationNotification(booking);
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      alert('Failed to update booking status');
    }
  };

  const sendCancellationNotification = async (booking: Booking) => {
    try {
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${SUPABASE_URL}/functions/v1/send-telegram-notification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tourName: booking.tours.name,
          bookingDate: new Date(booking.booking_date).toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          }),
          groupType: booking.group_type,
          persons: booking.persons,
          firstName: booking.first_name,
          lastName: booking.last_name,
          email: booking.email,
          phone: booking.phone,
          totalPrice: booking.total_price,
          paymentMethod: booking.payment_method,
          bookingCode: `${booking.booking_code} (CANCELLED BY ADMIN)`,
          bootSizes: booking.boot_sizes?.join(', '),
        }),
      });

      if (!response.ok) {
        console.error('Failed to send cancellation notification');
      }
    } catch (error) {
      console.error('Error sending cancellation notification:', error);
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleBooking || !newBookingDate || !newStartTime) {
      alert('Please select both a new date and time');
      return;
    }

    try {
      const { error } = await supabase
        .from('bookings')
        .update({
          booking_date: newBookingDate,
          start_time: newStartTime,
        })
        .eq('id', rescheduleBooking.id);

      if (error) throw error;

      setBookings(bookings.map(b =>
        b.id === rescheduleBooking.id ? { ...b, booking_date: newBookingDate, start_time: newStartTime } : b
      ));

      setRescheduleBooking(null);
      setNewBookingDate('');
      setNewStartTime('');
      alert('Booking rescheduled successfully');
    } catch (error) {
      console.error('Error rescheduling booking:', error);
      alert('Failed to reschedule booking');
    }
  };

  const deleteBooking = async (bookingId: string) => {
    if (!isSuperAdmin) {
      alert('Only superadmins can delete bookings');
      return;
    }

    if (!confirm('Are you sure you want to delete this booking? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('bookings')
        .delete()
        .eq('id', bookingId);

      if (error) throw error;

      setBookings(bookings.filter(b => b.id !== bookingId));
      if (selectedBooking?.id === bookingId) {
        setSelectedBooking(null);
      }
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert('Failed to delete booking');
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    const matchesSearch = searchTerm === '' ||
      booking.booking_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.tours.name.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesStatus && matchesSearch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-emerald-100 text-emerald-700 border-emerald-300';
      case 'not_confirmed':
        return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'cancelled':
        return 'bg-red-100 text-red-700 border-red-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'Confirmed';
      case 'not_confirmed':
        return 'Pending';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#2A382A]">Bookings Management</h1>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium">
              <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
              Pending: {bookings.filter(b => b.status === 'not_confirmed').length}
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-medium">
              <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
              Confirmed: {bookings.filter(b => b.status === 'confirmed').length}
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors font-medium"
          >
            <Plus className="w-5 h-5" />
            Add Booking
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200" style={{ width: '80vw', margin: '0 auto' }}>
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by booking code, name, email, or tour..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E]"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E]"
              >
                <option value="all">All Status</option>
                <option value="not_confirmed">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tour
                </th>
                <th className="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Group
                </th>
                <th className="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Commission
                </th>
                <th className="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredBookings.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-8 py-12 text-center text-gray-500">
                    No bookings found
                  </td>
                </tr>
              ) : (
                filteredBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-8 py-4">
                      <div className="text-sm font-medium text-[#2A382A]">{booking.tours.name}</div>
                      <div className="text-xs text-gray-500">{booking.group_type}</div>
                    </td>
                    <td className="px-8 py-4">
                      <div className="text-sm font-medium text-[#2A382A]">
                        {booking.first_name} {booking.last_name}
                      </div>
                      <div className="text-xs text-gray-500">{booking.email}</div>
                    </td>
                    <td className="px-8 py-4 whitespace-nowrap">
                      <div className="text-sm text-[#2A382A]">
                        {new Date(booking.booking_date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </div>
                      <div className="text-xs text-gray-500">{booking.start_time}</div>
                    </td>
                    <td className="px-8 py-4 whitespace-nowrap">
                      <div className="text-sm text-[#2A382A]">{booking.persons} persons</div>
                    </td>
                    <td className="px-8 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-[#2A382A]">
                        ${booking.total_price.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">{booking.payment_method}</div>
                    </td>
                    <td className="px-8 py-4 whitespace-nowrap">
                      {booking.status === 'cancelled' ? (
                        <div className="text-sm text-gray-400">--</div>
                      ) : (
                        <div className="text-sm font-medium text-[#953B0E]">
                          ${calculateCommission(booking).toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td className="px-8 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(booking.status)}`}>
                        {getStatusLabel(booking.status)}
                      </span>
                    </td>
                    <td className="px-8 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedBooking(booking)}
                          className="p-1.5 text-gray-600 hover:text-[#953B0E] hover:bg-gray-100 rounded transition-colors"
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {booking.status === 'not_confirmed' && (
                          <button
                            onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                            className="p-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded transition-colors"
                            title="Confirm booking"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        {booking.status !== 'cancelled' && (
                          <>
                            <button
                              onClick={() => {
                                setRescheduleBooking(booking);
                                setNewBookingDate(booking.booking_date.split('T')[0]);
                                setNewStartTime(booking.start_time);
                              }}
                              className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                              title="Reschedule booking"
                            >
                              <CalendarClock className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                              className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                              title="Cancel booking"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {isSuperAdmin && (
                          <button
                            onClick={() => deleteBooking(booking.id)}
                            className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            title="Delete booking (Superadmin only)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-[#2A382A]">Booking Details</h2>
                <button
                  onClick={() => setSelectedBooking(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-gray-500">Booking Code</span>
                  <p className="text-xl font-mono font-bold text-[#2A382A]">{selectedBooking.booking_code}</p>
                </div>
                <span className={`px-4 py-2 text-sm font-medium rounded-lg border ${getStatusColor(selectedBooking.status)}`}>
                  {getStatusLabel(selectedBooking.status)}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-5 h-5 text-[#953B0E]" />
                      <span className="font-semibold text-[#2A382A]">Tour Information</span>
                    </div>
                    <div className="pl-7 space-y-1">
                      <p className="text-sm"><span className="text-gray-500">Tour:</span> {selectedBooking.tours.name}</p>
                      <p className="text-sm"><span className="text-gray-500">Date:</span> {new Date(selectedBooking.booking_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
                      <p className="text-sm"><span className="text-gray-500">Time:</span> {selectedBooking.start_time}</p>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-5 h-5 text-[#953B0E]" />
                      <span className="font-semibold text-[#2A382A]">Group Details</span>
                    </div>
                    <div className="pl-7 space-y-1">
                      <p className="text-sm"><span className="text-gray-500">Type:</span> {selectedBooking.group_type}</p>
                      <p className="text-sm"><span className="text-gray-500">Persons:</span> {selectedBooking.persons}</p>
                      {selectedBooking.boot_sizes && selectedBooking.boot_sizes.length > 0 && (
                        <div className="text-sm">
                          <span className="text-gray-500">Boot Sizes:</span>
                          <div className="mt-1 space-y-0.5">
                            {selectedBooking.boot_sizes.map((size, index) => (
                              <p key={index} className="text-xs">Person {index + 1}: Size {size}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-5 h-5 text-[#953B0E]" />
                      <span className="font-semibold text-[#2A382A]">Payment</span>
                    </div>
                    <div className="pl-7 space-y-1">
                      <p className="text-sm"><span className="text-gray-500">Total:</span> ${selectedBooking.total_price.toFixed(2)}</p>
                      <p className="text-sm"><span className="text-gray-500">Method:</span> {selectedBooking.payment_method}</p>
                      {selectedBooking.status !== 'cancelled' && (
                        <>
                          <p className="text-sm pt-1"><span className="text-gray-500">Commission:</span> <span className="font-medium text-[#953B0E]">${calculateCommission(selectedBooking).toFixed(2)}</span></p>
                          <p className="text-xs text-gray-400">{getCommissionBreakdown(selectedBooking)}</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="w-5 h-5 text-[#953B0E]" />
                      <span className="font-semibold text-[#2A382A]">Customer Information</span>
                    </div>
                    <div className="pl-7 space-y-1">
                      <p className="text-sm"><span className="text-gray-500">Name:</span> {selectedBooking.first_name} {selectedBooking.last_name}</p>
                      <p className="text-sm"><span className="text-gray-500">Email:</span> {selectedBooking.email}</p>
                      <p className="text-sm"><span className="text-gray-500">Phone:</span> {selectedBooking.phone}</p>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-5 h-5 text-[#953B0E]" />
                      <span className="font-semibold text-[#2A382A]">Pickup Location</span>
                    </div>
                    <div className="pl-7">
                      <p className="text-sm">{selectedBooking.pickup_address}</p>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-[#953B0E]" />
                      <span className="font-semibold text-[#2A382A]">Booking Created</span>
                    </div>
                    <div className="pl-7">
                      <p className="text-sm">
                        {new Date(selectedBooking.created_at).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                {selectedBooking.status === 'not_confirmed' && (
                  <button
                    onClick={() => updateBookingStatus(selectedBooking.id, 'confirmed')}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Confirm Booking
                  </button>
                )}
                {selectedBooking.status !== 'cancelled' && (
                  <>
                    <button
                      onClick={() => {
                        setRescheduleBooking(selectedBooking);
                        setNewBookingDate(selectedBooking.booking_date.split('T')[0]);
                        setNewStartTime(selectedBooking.start_time);
                        setSelectedBooking(null);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <CalendarClock className="w-5 h-5" />
                      Reschedule
                    </button>
                    <button
                      onClick={() => updateBookingStatus(selectedBooking.id, 'cancelled')}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <XCircle className="w-5 h-5" />
                      Cancel Booking
                    </button>
                  </>
                )}
                <button
                  onClick={() => setSelectedBooking(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <CreateBookingModal
          tours={tours}
          onClose={() => setShowCreateModal(false)}
          onSuccess={fetchBookings}
        />
      )}

      {rescheduleBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-[#2A382A]">Reschedule Booking</h2>
              <button
                onClick={() => {
                  setRescheduleBooking(null);
                  setNewBookingDate('');
                  setNewStartTime('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-2">Current Booking:</p>
                <p className="font-medium text-[#2A382A]">{rescheduleBooking.tours.name}</p>
                <p className="text-sm text-gray-600">
                  {new Date(rescheduleBooking.booking_date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })} at {rescheduleBooking.start_time}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Date
                </label>
                <input
                  type="date"
                  value={newBookingDate}
                  onChange={(e) => setNewBookingDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Time
                </label>
                <input
                  type="time"
                  value={newStartTime}
                  onChange={(e) => setNewStartTime(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#953B0E]"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleReschedule}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Confirm Reschedule
                </button>
                <button
                  onClick={() => {
                    setRescheduleBooking(null);
                    setNewBookingDate('');
                    setNewStartTime('');
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsList;
